import React, { FunctionComponent, useEffect } from 'react';
import ReactModal from 'react-modal';

import CloseIcon from 'components/common/icons/CloseIcon';
import Button from 'components/common/buttons/Button';

import * as Styled from './styles/Modal.styles';

/**
 * ActionProps - discriminated union.
 * If `showAction` is not set, don't allow setting 'handleAction' and 'actionLabel' props
 * If `showAction` is set, require 'handleAction' prop
 */
export type PrimaryActionProps =
  | {
      showPrimaryAction: boolean;
      handlePrimaryAction: () => void;
      primaryActionLabel?: string;
      primaryActionLabelIcon?: React.ReactNode;
      primaryActionLabelAfterIcon?: React.ReactNode;
      primaryActionLoading?: boolean;
      primaryActionDisabled?: boolean;
      primaryActionRed?: boolean;
    }
  | {
      showPrimaryAction?: never;
      handlePrimaryAction?: never;
      primaryActionLabel?: never;
      primaryActionLabelIcon?: never;
      primaryActionLabelAfterIcon?: never;
      primaryActionLoading?: never;
      primaryActionDisabled?: never;
      primaryActionRed?: never;
    };

export type SecondaryActionProps =
  | {
      showSecondaryAction: boolean;
      handleSecondaryAction: () => void;
      secondaryActionLabel?: string;
      secondaryActionLabelIcon?: React.ReactNode;
      secondaryActionLoading?: boolean;
      secondaryActionDisabled?: boolean;
      secondaryActionRed?: boolean;
    }
  | {
      showSecondaryAction?: never;
      handleSecondaryAction?: never;
      secondaryActionLabel?: never;
      secondaryActionLabelIcon?: never;
      secondaryActionLoading?: never;
      secondaryActionDisabled?: never;
      secondaryActionRed?: never;
    };

/**
 * Base modal props.
 */
export interface ModalProps {
  isOpen: boolean;
  headerText?: string;
  onRequestClose: () => void;
  subHeaderText?: string;
  showCloseButton?: boolean;
  maxWidth?: string;
  height?: string;
  marginBottom?: string;
  maxHeight?: string;
  customHeader?: JSX.Element;
  heroImageUrl?: string;
  headerBadge?: JSX.Element;
  actionDivider?: boolean;
  actionBanner?: React.ReactNode;
  zIndex?: number;
  leftHeaderComponent?: React.ReactNode;
  borderRadius?: string;
}

/**
 * Base Modal component
 */
const Modal: FunctionComponent<
  React.PropsWithChildren<ModalProps & PrimaryActionProps & SecondaryActionProps>
> = ({
  isOpen,
  onRequestClose,
  headerText,
  children,
  subHeaderText = '',
  showCloseButton = false,
  showPrimaryAction = false,
  primaryActionLabel = 'Save',
  primaryActionLabelIcon = null,
  primaryActionLabelAfterIcon = null,
  primaryActionRed = false,
  handlePrimaryAction,
  primaryActionLoading,
  primaryActionDisabled,
  showSecondaryAction = false,
  secondaryActionLabel = 'Cancel',
  secondaryActionLabelIcon = null,
  secondaryActionRed = false,
  handleSecondaryAction,
  secondaryActionLoading,
  actionBanner,
  secondaryActionDisabled,
  maxWidth = 'auto',
  height = 'fit-content',
  maxHeight = '100vh',
  customHeader = null,
  heroImageUrl = null,
  headerBadge = null,
  actionDivider = false,
  marginBottom = '16px',
  zIndex = 1500,
  leftHeaderComponent,
  borderRadius
}) => {
  const shouldRenderActions =
    (showPrimaryAction && handlePrimaryAction) || (showSecondaryAction && handleSecondaryAction);

  const renderHeader = (): JSX.Element => {
    if (customHeader) return customHeader;

    return (
      <Styled.HeaderContainer>
        {headerText && <Styled.HeaderText>{headerText}</Styled.HeaderText>}
      </Styled.HeaderContainer>
    );
  };

  /**
   * Disable background scroll when modal is open
   */
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }

    return (): void => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      overlayClassName="_"
      className="_"
      overlayElement={(props, contentElement): React.ReactElement => (
        <Styled.ModalOverlay {...props}>{contentElement}</Styled.ModalOverlay>
      )}
      contentElement={(props, modalChildren): React.ReactElement => (
        <Styled.ModalContentContainer {...props} height={height}>
          {modalChildren}
        </Styled.ModalContentContainer>
      )}
      shouldCloseOnOverlayClick={showCloseButton}
      shouldCloseOnEsc={showCloseButton}
      style={{
        content: { maxWidth },
        overlay: {
          zIndex
        }
      }}
      appElement={document.body}
    >
      <Styled.ModalContentWrapper
        maxHeight={maxHeight}
        height={height}
        marginBottom={marginBottom}
        borderRadius={borderRadius}
      >
        {leftHeaderComponent}
        {/* Close button */}
        {showCloseButton && (
          <Styled.ModalCloseButton
            onClick={onRequestClose}
            data-test="close-btn"
            data-testid="modal-close-btn"
          >
            <CloseIcon />
          </Styled.ModalCloseButton>
        )}

        {/* Hero image */}
        {!!heroImageUrl && heroImageUrl.trim().length && (
          <div className="tw-mx-[-24px] tw-mb-6 tw-mt-[-24px]">
            <img className="tw-w-full tw-rounded-t-[4px]" src={heroImageUrl} alt={headerText} />
          </div>
        )}
        {(headerBadge || customHeader || headerText || subHeaderText) && (
          <div className="tw-mb-6">
            {/* Header badge */}
            {headerBadge && <div className="tw-mb-2">{headerBadge}</div>}

            {/* Header */}
            {renderHeader()}

            {/* Subheader text */}
            {subHeaderText && <Styled.SubHeader>{subHeaderText}</Styled.SubHeader>}
          </div>
        )}

        {children}
        {actionDivider && (
          <div className="tw--ml-6 tw--mr-6 tw-mt-6 tw-border-b-0 tw-border-t tw-border-solid tw-border-border-grey" />
        )}

        {/* Actions */}
        {shouldRenderActions && (
          <Styled.ActionContainer>
            {actionBanner && <div className="tw-mb-4 tw-mr-auto sm:tw-mb-0 ">{actionBanner}</div>}
            <Styled.ActionButtonContainer>
              {showSecondaryAction && handleSecondaryAction && (
                <Button
                  secondary={!secondaryActionRed}
                  onClick={(): void => handleSecondaryAction()}
                  isLoading={secondaryActionLoading}
                  isDisabled={secondaryActionDisabled}
                  icon={secondaryActionLabelIcon}
                  red={secondaryActionRed}
                >
                  {secondaryActionLabel}
                </Button>
              )}
              {showPrimaryAction && handlePrimaryAction && (
                <Button
                  onClick={(): void => handlePrimaryAction()}
                  isLoading={primaryActionLoading}
                  isDisabled={primaryActionDisabled}
                  icon={primaryActionLabelIcon}
                  afterIcon={primaryActionLabelAfterIcon}
                  red={primaryActionRed}
                >
                  {primaryActionLabel}
                </Button>
              )}
            </Styled.ActionButtonContainer>
          </Styled.ActionContainer>
        )}
      </Styled.ModalContentWrapper>
    </ReactModal>
  );
};

export default Modal;
